import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";
import { Search, ChevronLeft, ChevronRight, KeyRound, Trash2, RefreshCw, X, Loader2, Link as LinkIcon, Ticket, Copy, Shield } from "lucide-react";

const PLAN_LABELS = {
  indian_monthly: "Indian · Monthly", indian_annual: "Indian · Annual",
  all_monthly: "All Mkts · Monthly", all_annual: "All Mkts · Annual",
  monthly: "Legacy Monthly", annual: "Legacy Annual",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return "—"; }
}

function StatusBadge({ status }) {
  const map = {
    active: "border-[#22C55E]/50 text-[#22C55E]",
    trialing: "border-[#F59E0B]/50 text-[#F59E0B]",
    free: "border-zinc-700 text-zinc-400",
  };
  return <span className={`px-1.5 py-0.5 border text-[9px] uppercase tracking-widest font-mono ${map[status] || map.free}`}>{status}</span>;
}

function ResetPwModal({ target, onClose }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${target.user_id}/reset-password`, { new_password: pw });
      toast.success(`Password reset for ${target.email} — share it with the user securely.`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[85] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="reset-pw-modal">
      <div className="bg-[#0a0a0a] border border-zinc-800 max-w-sm w-full p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-base font-bold uppercase tracking-tight">Reset password</h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="reset-pw-close"><X className="h-4 w-4" /></button>
        </div>
        <div className="text-xs text-zinc-400">Set a temporary password for <span className="text-white font-mono">{target.email}</span>. Passwords are stored one-way hashed — this is the only safe way to help a locked-out user.</div>
        <input
          type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New temporary password (min 6 chars)"
          className="w-full bg-[#0a0a0a] border border-zinc-800 focus:border-[#007AFF] px-3 py-2 text-sm font-mono outline-none"
          data-testid="reset-pw-input"
        />
        <button onClick={submit} disabled={busy || pw.length < 6}
                className="w-full text-xs uppercase tracking-widest bg-[#007AFF] hover:bg-[#3395FF] disabled:opacity-50 text-white py-2.5 font-mono font-bold"
                data-testid="reset-pw-submit">
          {busy ? "Saving…" : "Set new password"}
        </button>
      </div>
    </div>
  );
}

function GatePanel() {
  const [data, setData] = useState(null);
  const [label, setLabel] = useState("");
  const [customPw, setCustomPw] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/gate/passwords").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/admin/gate/passwords", { label, password: customPw || null });
      await navigator.clipboard.writeText(d.password).catch(() => {});
      toast.success(`Access password created & copied: ${d.password}`, { duration: 8000 });
      setLabel(""); setCustomPw(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };
  const items = data?.items || [];
  return (
    <div className="mt-10 border border-zinc-900" data-testid="admin-gate-panel">
      <div className="px-4 py-3 border-b border-zinc-900 flex items-center gap-2 flex-wrap">
        <Shield className="h-4 w-4 text-[#007AFF]" />
        <h2 className="font-heading text-base font-bold uppercase tracking-tight">Site gate passwords</h2>
        <span className={`px-1.5 py-0.5 border text-[9px] uppercase tracking-widest font-mono ${data?.enabled ? "border-[#22C55E]/50 text-[#22C55E]" : "border-[#EF4444]/50 text-[#EF4444]"}`} data-testid="gate-enabled-badge">
          {data ? (data.enabled ? "Gate ON" : "Gate OFF") : "…"}
        </span>
        <span className="text-[9px] font-mono text-zinc-600 uppercase tracking-widest ml-2">unlocks expire daily at midnight IST · revoke locks devices out instantly</span>
      </div>
      <div className="px-4 py-4 flex flex-wrap items-end gap-3">
        <div className="text-[10px] font-mono text-zinc-500 uppercase flex-1 min-w-[180px]">
          <div className="mb-1">Label (who's it for)</div>
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Telegram Batch 1"
                 className="w-full bg-[#0e0e0e] border border-zinc-800 px-2 py-2 text-white normal-case outline-none focus:border-[#007AFF]" data-testid="gate-label-input" />
        </div>
        <div className="text-[10px] font-mono text-zinc-500 uppercase flex-1 min-w-[180px]">
          <div className="mb-1">Custom password (blank = auto-generate)</div>
          <input value={customPw} onChange={(e) => setCustomPw(e.target.value)} placeholder="Leave blank to generate"
                 className="w-full bg-[#0e0e0e] border border-zinc-800 px-2 py-2 text-white normal-case outline-none focus:border-[#007AFF]" data-testid="gate-custom-pw-input" />
        </div>
        <button onClick={create} disabled={busy}
                className="bg-[#007AFF] hover:bg-[#3395FF] disabled:opacity-50 text-white text-[10px] uppercase tracking-widest font-mono font-bold px-5 py-2.5"
                data-testid="gate-generate-btn">
          {busy ? "Creating…" : "Generate & copy"}
        </button>
      </div>
      <div className="overflow-x-auto max-h-72 overflow-y-auto">
        <table className="w-full text-xs">
          <thead><tr className="bg-[#0e0e0e] text-[9px] uppercase tracking-widest text-zinc-500 font-mono">
            {["Password", "Label", "Status", "Unlocks", "Created", ""].map((h) => <th key={h} className="px-3 py-2 text-left">{h}</th>)}
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-zinc-600 font-mono uppercase text-[10px]" data-testid="gate-empty">No managed passwords yet — the master admin password still works</td></tr>}
            {items.map((g) => (
              <tr key={g.pid} className="border-t border-zinc-900" data-testid={`gate-row-${g.pid}`}>
                <td className="px-3 py-2 font-mono font-bold text-white">
                  {g.password}
                  <button onClick={() => { navigator.clipboard.writeText(g.password); toast.success("Copied"); }} className="ml-2 text-zinc-600 hover:text-white" data-testid={`gate-copy-${g.pid}`}><Copy className="h-3 w-3 inline" /></button>
                </td>
                <td className="px-3 py-2 text-zinc-400">{g.label || "—"}</td>
                <td className="px-3 py-2">
                  {g.revoked ? <span className="text-zinc-600 font-mono text-[9px] uppercase">revoked</span>
                    : <span className="text-[#22C55E] font-mono text-[9px] uppercase">active</span>}
                </td>
                <td className="px-3 py-2 font-mono text-zinc-500">{g.unlock_count || 0}</td>
                <td className="px-3 py-2 font-mono text-zinc-500">{fmtDate(g.created_at)}</td>
                <td className="px-3 py-2">
                  {!g.revoked && (
                    <button onClick={async () => {
                              if (!window.confirm(`Revoke "${g.password}"? Every device that unlocked with it gets locked out.`)) return;
                              try { await api.post(`/admin/gate/passwords/${g.pid}/revoke`); toast.success("Revoked"); load(); }
                              catch (e) { toast.error(e?.response?.data?.detail || "Revoke failed"); }
                            }}
                            className="text-[9px] font-mono uppercase border border-zinc-800 hover:border-[#EF4444]/60 text-zinc-500 hover:text-[#EF4444] px-2 py-1"
                            data-testid={`gate-revoke-${g.pid}`}>Revoke</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CouponsPanel() {
  const [items, setItems] = useState([]);
  const [pct, setPct] = useState(20);
  const [count, setCount] = useState(1);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/coupons").then((r) => setItems(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const gen = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/coupons/generate", { percent_off: Number(pct), count: Number(count), note, expires_days: 90, max_uses: 1 });
      await navigator.clipboard.writeText(data.codes.join("\n")).catch(() => {});
      toast.success(`${data.codes.length} coupon(s) generated & copied: ${data.codes.join(", ")}`, { duration: 8000 });
      setNote(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Generate failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-10 border border-zinc-900" data-testid="admin-coupons-panel">
      <div className="px-4 py-3 border-b border-zinc-900 flex items-center gap-2">
        <Ticket className="h-4 w-4 text-[#F59E0B]" />
        <h2 className="font-heading text-base font-bold uppercase tracking-tight">Discount coupons</h2>
        <span className="text-[9px] font-mono text-zinc-600 uppercase tracking-widest ml-2">unique · single-use · 90-day validity</span>
      </div>
      <div className="px-4 py-4 flex flex-wrap items-end gap-3">
        <div className="text-[10px] font-mono text-zinc-500 uppercase">
          <div className="mb-1">% off</div>
          <input type="number" min="1" max="100" value={pct} onChange={(e) => setPct(e.target.value)}
                 className="w-20 bg-[#0e0e0e] border border-zinc-800 px-2 py-2 text-white" data-testid="coupon-pct" />
        </div>
        <div className="text-[10px] font-mono text-zinc-500 uppercase">
          <div className="mb-1">How many</div>
          <input type="number" min="1" max="50" value={count} onChange={(e) => setCount(e.target.value)}
                 className="w-20 bg-[#0e0e0e] border border-zinc-800 px-2 py-2 text-white" data-testid="coupon-count" />
        </div>
        <div className="text-[10px] font-mono text-zinc-500 uppercase flex-1 min-w-[180px]">
          <div className="mb-1">Note (who's it for)</div>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Launch promo / Mr Sharma"
                 className="w-full bg-[#0e0e0e] border border-zinc-800 px-2 py-2 text-white normal-case" data-testid="coupon-note" />
        </div>
        <button onClick={gen} disabled={busy}
                className="bg-[#F59E0B] hover:bg-[#fbbf24] disabled:opacity-50 text-black text-[10px] uppercase tracking-widest font-mono font-bold px-5 py-2.5"
                data-testid="coupon-generate">
          {busy ? "Generating…" : "Generate & copy"}
        </button>
      </div>
      <div className="overflow-x-auto max-h-72 overflow-y-auto">
        <table className="w-full text-xs">
          <thead><tr className="bg-[#0e0e0e] text-[9px] uppercase tracking-widest text-zinc-500 font-mono">
            {["Code", "% off", "Note", "Status", "Used by", "Expires", ""].map((h) => <th key={h} className="px-3 py-2 text-left">{h}</th>)}
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-zinc-600 font-mono uppercase text-[10px]">No coupons yet</td></tr>}
            {items.map((c) => {
              const spent = (c.used_by || []).length >= c.max_uses;
              return (
                <tr key={c.code} className="border-t border-zinc-900" data-testid={`coupon-row-${c.code}`}>
                  <td className="px-3 py-2 font-mono font-bold text-white">
                    {c.code}
                    <button onClick={() => { navigator.clipboard.writeText(c.code); toast.success("Copied"); }} className="ml-2 text-zinc-600 hover:text-white"><Copy className="h-3 w-3 inline" /></button>
                  </td>
                  <td className="px-3 py-2 font-mono text-[#F59E0B]">{c.percent_off}%</td>
                  <td className="px-3 py-2 text-zinc-400">{c.note || "—"}</td>
                  <td className="px-3 py-2">
                    {!c.active ? <span className="text-zinc-600 font-mono text-[9px] uppercase">deactivated</span>
                      : spent ? <span className="text-[#F59E0B] font-mono text-[9px] uppercase">used</span>
                        : <span className="text-[#22C55E] font-mono text-[9px] uppercase">active</span>}
                  </td>
                  <td className="px-3 py-2 font-mono text-zinc-500">{(c.used_by || []).length}/{c.max_uses}</td>
                  <td className="px-3 py-2 font-mono text-zinc-500">{fmtDate(c.expires_at)}</td>
                  <td className="px-3 py-2">
                    {c.active && !spent && (
                      <button onClick={async () => { await api.post(`/admin/coupons/${c.code}/deactivate`); toast.success("Deactivated"); load(); }}
                              className="text-[9px] font-mono uppercase border border-zinc-800 hover:border-[#EF4444]/60 text-zinc-500 hover:text-[#EF4444] px-2 py-1"
                              data-testid={`coupon-deactivate-${c.code}`}>Kill</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminUsersPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [plan, setPlan] = useState("");
  const [status, setStatus] = useState("");
  const [scope, setScope] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const pageSize = 25;

  useEffect(() => {
    const id = setTimeout(() => { setDebounced(search); setPage(1); }, 400);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => {
    if (!authLoading && user && !user.is_admin) navigate("/dashboard");
  }, [authLoading, user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
      if (debounced) p.set("search", debounced);
      if (plan) p.set("plan", plan);
      if (status) p.set("status", status);
      if (scope) p.set("scope", scope);
      const { data: d } = await api.get(`/admin/users?${p.toString()}`);
      setData(d);
    } catch { toast.error("Could not load users"); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    if (user?.is_admin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.is_admin, debounced, plan, status, scope, page]);

  const act = async (fn, okMsg) => {
    try { await fn(); toast.success(okMsg); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  if (authLoading || !user) return <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center font-mono text-sm uppercase tracking-widest">◢ Loading…</div>;
  if (!user.is_admin) return null;

  const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar variant="dashboard" />
      <div className="px-4 md:px-8 py-8 max-w-[1400px] mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#007AFF] font-mono">◢ Admin · Mission control</div>
            <h1 className="font-heading text-3xl font-black uppercase tracking-tight" data-testid="admin-users-heading">User Management</h1>
          </div>
          <div className="text-xs font-mono uppercase tracking-widest text-zinc-500" data-testid="admin-users-total">
            {data ? `${data.total} user${data.total === 1 ? "" : "s"}` : ""}
          </div>
        </div>

        {/* Search + filters */}
        <div className="mt-6 flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-600" />
            <input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email or user ID…"
              className="w-full bg-[#0e0e0e] border border-zinc-800 focus:border-[#007AFF] pl-9 pr-3 py-2.5 text-sm outline-none font-mono"
              data-testid="admin-users-search"
            />
          </div>
          {[["plan", plan, setPlan, [["", "All plans"], ["pro", "Pro"], ["free", "Free"]]],
            ["status", status, setStatus, [["", "All statuses"], ["active", "Active"], ["trialing", "Trialing"], ["free", "Free"]]],
            ["scope", scope, setScope, [["", "All scopes"], ["indian", "Indian only"], ["all", "All markets"]]]]
            .map(([key, val, setter, opts]) => (
              <select key={key} value={val} onChange={(e) => { setter(e.target.value); setPage(1); }}
                      className="bg-[#0e0e0e] border border-zinc-800 px-3 py-2.5 text-xs font-mono uppercase tracking-widest outline-none focus:border-[#007AFF]"
                      data-testid={`admin-users-filter-${key}`}>
                {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            ))}
          <button onClick={load} className="text-zinc-500 hover:text-white p-2 border border-zinc-800" title="Refresh" data-testid="admin-users-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>

        {/* Table */}
        <div className="mt-4 border border-zinc-900 overflow-x-auto">
          <table className="w-full text-left text-xs" data-testid="admin-users-table">
            <thead>
              <tr className="bg-[#0e0e0e] text-[9px] uppercase tracking-widest text-zinc-500 font-mono">
                {["User", "User ID", "Plan", "Status", "Scope", "Expires", "Auto-renew", "DHAN", "Joined", "Actions"].map((h) => (
                  <th key={h} className="px-3 py-2.5 font-normal whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && !data && (
                <tr><td colSpan={10} className="px-3 py-8 text-center font-mono uppercase tracking-widest text-zinc-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading users…</td></tr>
              )}
              {data?.items?.length === 0 && (
                <tr><td colSpan={10} className="px-3 py-8 text-center font-mono uppercase tracking-widest text-zinc-500" data-testid="admin-users-empty">No users match this search</td></tr>
              )}
              {data?.items?.map((u) => (
                <tr key={u.user_id} className="border-t border-zinc-900 hover:bg-[#0e0e0e]" data-testid={`admin-user-row-${u.user_id}`}>
                  <td className="px-3 py-2.5">
                    <div className="font-bold text-white truncate max-w-[180px]">{u.name || "—"} {u.is_admin && <span className="text-[8px] text-[#007AFF] border border-[#007AFF]/40 px-1 ml-1 uppercase">admin</span>}</div>
                    <div className="text-zinc-500 font-mono truncate max-w-[180px]">{u.email}</div>
                  </td>
                  <td className="px-3 py-2.5 font-mono text-zinc-500 whitespace-nowrap">{u.user_id}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <span className={u.plan === "pro" ? "text-[#22C55E] font-bold" : "text-zinc-400"}>{u.plan.toUpperCase()}</span>
                    {u.plan_tier && <div className="text-zinc-600 text-[10px]">{PLAN_LABELS[u.plan_tier] || u.plan_tier}</div>}
                  </td>
                  <td className="px-3 py-2.5"><StatusBadge status={u.status} /></td>
                  <td className="px-3 py-2.5 font-mono text-zinc-400 uppercase text-[10px]">{u.market_scope === "indian" ? "🇮🇳 Indian" : "🌍 All"}</td>
                  <td className="px-3 py-2.5 font-mono text-zinc-400 whitespace-nowrap">
                    {fmtDate(u.plan_expires_at)}
                    {u.status === "trialing" && <div className="text-[#F59E0B] text-[10px]">trial ends {fmtDate(u.trial_ends_at)}</div>}
                  </td>
                  <td className="px-3 py-2.5">
                    <button
                      onClick={() => act(() => api.post(`/admin/users/${u.user_id}/auto-renew`, { enabled: !u.auto_renew }), `Auto-renew ${!u.auto_renew ? "ON" : "OFF"} for ${u.email}`)}
                      className={`px-2 py-0.5 border text-[9px] uppercase tracking-widest font-mono ${u.auto_renew ? "border-[#22C55E]/50 text-[#22C55E]" : "border-zinc-700 text-zinc-500"}`}
                      data-testid={`auto-renew-toggle-${u.user_id}`}
                    >
                      {u.auto_renew ? "ON" : "OFF"}
                    </button>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-block h-2 w-2 rounded-full ${u.dhan_connected ? "bg-[#22C55E]" : "bg-zinc-700"}`} title={u.dhan_connected ? "DHAN connected" : "DHAN not connected"} />
                  </td>
                  <td className="px-3 py-2.5 font-mono text-zinc-500 whitespace-nowrap">{fmtDate(u.created_at)}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <button onClick={() => act(() => api.post(`/admin/users/${u.user_id}/plan`, { action: "extend", days: 30 }), `Extended ${u.email} by 30 days`)}
                              className="px-1.5 py-1 border border-zinc-800 hover:border-[#22C55E]/60 text-zinc-400 hover:text-[#22C55E] text-[9px] font-mono uppercase" title="Extend plan 30 days" data-testid={`extend-30-${u.user_id}`}>+30d</button>
                      <button onClick={() => act(() => api.post(`/admin/users/${u.user_id}/plan`, { action: "extend", days: 365 }), `Extended ${u.email} by 1 year`)}
                              className="px-1.5 py-1 border border-zinc-800 hover:border-[#22C55E]/60 text-zinc-400 hover:text-[#22C55E] text-[9px] font-mono uppercase" title="Extend plan 1 year" data-testid={`extend-365-${u.user_id}`}>+1y</button>
                      {!u.is_admin && (
                        <button onClick={() => { if (window.confirm(`Downgrade ${u.email} to FREE immediately?`)) act(() => api.post(`/admin/users/${u.user_id}/plan`, { action: "set_free" }), `${u.email} set to Free`); }}
                                className="px-1.5 py-1 border border-zinc-800 hover:border-[#F59E0B]/60 text-zinc-400 hover:text-[#F59E0B] text-[9px] font-mono uppercase" title="Downgrade to Free" data-testid={`set-free-${u.user_id}`}>Free</button>
                      )}
                      <button onClick={() => setResetTarget(u)} className="p-1 border border-zinc-800 hover:border-[#007AFF]/60 text-zinc-400 hover:text-[#3B9BFF]" title="Reset password" data-testid={`reset-pw-${u.user_id}`}>
                        <KeyRound className="h-3 w-3" />
                      </button>
                      <button onClick={async () => {
                                try {
                                  const { data: d } = await api.post(`/admin/users/${u.user_id}/reset-link`);
                                  const link = `${window.location.origin}${d.path}`;
                                  await navigator.clipboard.writeText(link);
                                  toast.success(`Reset link for ${d.email} copied to clipboard — valid 24h. Send it via email/WhatsApp.`, { duration: 8000 });
                                } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate link"); }
                              }}
                              className="p-1 border border-zinc-800 hover:border-[#22C55E]/60 text-zinc-400 hover:text-[#22C55E]" title="Copy password-reset link (24h)" data-testid={`reset-link-${u.user_id}`}>
                        <LinkIcon className="h-3 w-3" />
                      </button>
                      {!u.is_admin && (
                        <button onClick={() => { if (window.confirm(`DELETE ${u.email} permanently? This removes their watchlist, subscriptions and broker connection.`)) act(() => api.delete(`/admin/users/${u.user_id}`), `${u.email} deleted`); }}
                                className="p-1 border border-zinc-800 hover:border-[#EF4444]/60 text-zinc-400 hover:text-[#EF4444]" title="Delete user" data-testid={`delete-user-${u.user_id}`}>
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="mt-4 flex items-center justify-between text-xs font-mono uppercase tracking-widest text-zinc-500">
          <div data-testid="admin-users-page-info">Page {page} of {totalPages}</div>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="p-1.5 border border-zinc-800 hover:border-zinc-600 disabled:opacity-40" data-testid="admin-users-prev"><ChevronLeft className="h-4 w-4" /></button>
            <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="p-1.5 border border-zinc-800 hover:border-zinc-600 disabled:opacity-40" data-testid="admin-users-next"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>

        <GatePanel />

        <CouponsPanel />

        <div className="mt-6 text-[10px] text-zinc-600 font-mono uppercase tracking-widest leading-relaxed max-w-3xl">
          Auto-renew: when ON, an expired plan renews automatically for the same tier and a payment record is logged (Razorpay MOCKED — plugs into live Razorpay subscriptions on go-live). Passwords are one-way hashed and can only be reset, never viewed.
        </div>
      </div>

      {resetTarget && <ResetPwModal target={resetTarget} onClose={() => setResetTarget(null)} />}
    </div>
  );
}
